/**
 * Summit Service - Registration management for YOCILAB Youth Summit 2026
 * Handles client-side validation, localStorage persistence, calendar exports, and submission processing.
 */

export const SUMMIT_EVENT_DETAILS = {
  name: 'YOCILAB Youth Summit 2026',
  theme: 'Connecting Talent to Opportunity',
  dates: '30th and 31st of October 2026',
  startDate: '2026-10-30T08:00:00+01:00',
  endDate: '2026-10-31T18:00:00+01:00',
  location: 'Bamenda, Cameroon',
  venue: 'Bamenda Congress Hall / Regional Hub, Bamenda, Cameroon',
  organizer: 'Youth Connect Innovation Lab (YOCILAB)',
  contactEmail: 'summit@yocilab.org',
  whatsappHelp: '+237 680 192 095'
};

export const GOOGLE_SHEETS_WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbxoLQISXNm67llf5maHGltOUM3givmru_rKkpb_vI1ktYAk3Zk4CDRyS2Ug7qNm3NfAsg/exec';

const REGISTRATION_STORAGE_KEY = 'yocilab_summit_registrations';

/**
 * Generate a unique Summit Delegate ID (e.g., YYS26-D8942)
 */
export const generateRegistrationId = (type = 'delegate') => {
  const prefixMap = {
    delegate: 'DEL',
    challenge: 'HACK',
    scholarship: 'SCH',
    exhibitor: 'EXH',
    partner: 'PTR',
    volunteer: 'VOL'
  };
  const prefix = prefixMap[type] || 'DEL';
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  const randomChar = String.fromCharCode(65 + Math.floor(Math.random() * 26));
  return `YYS26-${prefix}-${randomChar}${randomNum}`;
};

/**
 * Validate summit registration form
 */
export const validateSummitRegistration = (formData) => {
  const errors = {};

  // Validate Name
  if (!formData.fullName?.trim()) {
    errors.fullName = 'Full name is required';
  } else if (formData.fullName.trim().length < 2) {
    errors.fullName = 'Name must be at least 2 characters';
  }

  // Validate Email
  if (!formData.email?.trim()) {
    errors.email = 'Email address is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
    errors.email = 'Please enter a valid email address';
  }

  // Validate Phone
  if (!formData.phone?.trim()) {
    errors.phone = 'Phone / WhatsApp number is required';
  } else if (formData.phone.trim().replace(/\D/g, '').length < 8) {
    errors.phone = 'Please enter a valid phone number (at least 8 digits)';
  }

  // Validate City
  if (!formData.city?.trim()) {
    errors.city = 'Please specify your city or region';
  }

  // Validate Category / Status
  if (!formData.category) {
    errors.category = 'Please select your current role or status';
  }

  // Validate Organization
  if (!formData.organization?.trim()) {
    errors.organization = 'Please enter your school, university, or organization name';
  }

  // Conditional validation based on registration type
  if (formData.registrationType === 'challenge') {
    if (!formData.teamName?.trim() && formData.isTeamLead) {
      errors.teamName = 'Team name is required for team submissions';
    }
    if (!formData.projectIdea?.trim()) {
      errors.projectIdea = 'Please provide a brief description of your innovation/idea';
    }
  }

  if (formData.registrationType === 'scholarship') {
    if (!formData.scholarshipStatement?.trim() || formData.scholarshipStatement.trim().length < 20) {
      errors.scholarshipStatement = 'Please provide a statement explaining why you need a scholarship (at least 20 characters)';
    }
  }

  // Paid tracks cannot be confirmed until payment has been declared.
  if (formData.registrationType !== 'scholarship' && !formData.paymentConfirmed) {
    errors.paymentConfirmed = 'Please pay the 2,000 CFA fee and send the screenshot on WhatsApp before registering.';
  }

  // Terms acceptance
  if (!formData.agreedToTerms) {
    errors.agreedToTerms = 'You must confirm your attendance and agree to the event guidelines';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Sanitize registration data
 */
export const sanitizeRegistrationData = (formData) => {
  return {
    registrationType: formData.registrationType || 'delegate',
    fullName: formData.fullName?.trim() || '',
    email: formData.email?.trim().toLowerCase() || '',
    phone: formData.phone?.trim() || '',
    city: formData.city?.trim() || '',
    country: formData.country?.trim() || 'Cameroon',
    category: formData.category || '',
    organization: formData.organization?.trim() || '',
    trackInterest: formData.trackInterest || 'Employability & Careers',
    teamName: formData.teamName?.trim() || '',
    teamSize: formData.teamSize || '1',
    isTeamLead: !!formData.isTeamLead,
    projectIdea: formData.projectIdea?.trim() || '',
    scholarshipStatement: formData.scholarshipStatement?.trim() || '',
    dietaryOrAccessibility: formData.dietaryOrAccessibility?.trim() || '',
    referralSource: formData.referralSource || 'Social Media',
    notes: formData.notes?.trim() || '',
    paymentConfirmed: !!formData.paymentConfirmed,
    agreedToTerms: !!formData.agreedToTerms
  };
};

/**
 * Submit registration
 */
export const submitSummitRegistration = async (formData) => {
  try {
    const sanitized = sanitizeRegistrationData(formData);
    const validation = validateSummitRegistration(sanitized);

    if (!validation.isValid) {
      return {
        success: false,
        errors: validation.errors,
        message: 'Please complete all required fields correctly.'
      };
    }

    const registrationId = generateRegistrationId(sanitized.registrationType);
    const submittedAt = new Date().toISOString();

    const record = {
      ...sanitized,
      registrationId,
      submittedAt,
      summitDates: SUMMIT_EVENT_DETAILS.dates,
      venue: SUMMIT_EVENT_DETAILS.venue
    };

    // Save to localStorage
    try {
      const existingStr = localStorage.getItem(REGISTRATION_STORAGE_KEY);
      const existing = existingStr ? JSON.parse(existingStr) : [];
      existing.unshift(record);
      localStorage.setItem(REGISTRATION_STORAGE_KEY, JSON.stringify(existing));
    } catch (storageErr) {
      console.warn('LocalStorage error saving registration:', storageErr);
    }

    // Send payload directly to Google Sheets Webhook
    if (GOOGLE_SHEETS_WEBHOOK_URL) {
      try {
        await fetch(GOOGLE_SHEETS_WEBHOOK_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'text/plain;charset=utf-8'
          },
          body: JSON.stringify({
            formType: 'summit',
            ...record
          })
        });
        console.info('Sent registration record to Google Sheets webhook');
      } catch (webhookErr) {
        console.warn('Webhook transmission error (data still secured in localStorage):', webhookErr);
      }
    }

    // Log to console
    console.info('Youth Summit 2026 Registration successful:', record);

    return {
      success: true,
      registrationId,
      record,
      message: `Registration confirmed! Your Delegate ID is ${registrationId}. We look forward to welcoming you on 30th & 31st October 2026 in Bamenda.`
    };
  } catch (error) {
    console.error('Registration processing error:', error);
    return {
      success: false,
      message: 'Failed to process registration. Please try again or reach out directly at summit@yocilab.org'
    };
  }
};

/**
 * Generate .ics calendar download
 */
export const generateCalendarICS = () => {
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//YOCILAB//Youth Summit 2026//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    'SUMMARY:YOCILAB Youth Summit 2026 (30th - 31st Oct)',
    'DESCRIPTION:Africa\\\'s Emerging Platform for Employability\\, Innovation and Youth Leadership. 500+ delegates in Bamenda\\, Cameroon.',
    'LOCATION:Bamenda Congress Hall\\, Bamenda\\, Cameroon',
    'DTSTART:20261030T070000Z',
    'DTEND:20261031T170000Z',
    'STATUS:CONFIRMED',
    'ORGANIZER;CN=YOCILAB:MAILTO:summit@yocilab.org',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute('download', 'YOCILAB_Youth_Summit_2026.ics');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
